// The set-up steps for a site, with the list of steps down the side (as in the
// approved mock-up). Each step says whether it's done, from what the site already has.

import type { ReactNode } from 'react';
import { languageProgress, type SiteDraft } from '../../../../src/draft.ts';
import type { SiteView } from '../../types.ts';
import { Link, Shell } from '../../ui.tsx';

export const STEPS = [
  ['details', 'Client and site'],
  ['map', 'Site on the map'],
  ['zones', 'Zones'],
  ['wording', 'Alert wording'],
  ['tablet', 'Ops tablet'],
  ['review', 'Review and send'],
  ['poster', 'QR code and poster'],
  ['test', 'First test'],
] as const;
export type StepId = (typeof STEPS)[number][0];

export function stepDone(site: SiteView, step: StepId): boolean {
  const d = site.draft as SiteDraft;
  switch (step) {
    case 'details':
      return true;
    case 'map':
      return !!d.map && !!d.picture;
    case 'zones':
      return d.zones.length > 0;
    case 'wording':
      return Object.keys(d.messages).every((l) => d.checked.includes(l) && languageProgress(d, l).complete);
    case 'tablet':
      return d.tablets.length > 0;
    case 'review':
      return site.changes.some((c) => c.status === 'waiting' || c.status === 'approved');
    case 'poster':
      return site.live;
    case 'test':
      return site.live && !!site.joined.lastTest;
  }
}

export function SetupShell({ site, step, children }: { site: SiteView; step: StepId; children: ReactNode }) {
  const index = STEPS.findIndex(([id]) => id === step);
  const next = STEPS[index + 1];
  const prev = STEPS[index - 1];
  return (
    <Shell>
      <div className="head">
        <div className="head-title">
          <Link to={`/sites/${site.siteId}`} className="muted small">
            ‹ {site.siteName}
          </Link>
          <h1>{STEPS[index][1]}</h1>
        </div>
        <span className="faint">
          Step {index + 1} of {STEPS.length}
        </span>
      </div>
      <div className="setup">
        <ol className="steps" aria-label="Set-up steps">
          {STEPS.map(([id, label], i) => {
            const done = stepDone(site, id);
            return (
              <li key={id}>
                <Link to={`/sites/${site.siteId}/setup/${id}`} className={id === step ? 'step on' : done ? 'step done' : 'step'}>
                  <span className="step-mark">{done && id !== step ? '✓' : i + 1}</span>
                  {label}
                </Link>
              </li>
            );
          })}
        </ol>
        <section className="setup-body">
          {children}
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
            {prev ? (
              <Link to={`/sites/${site.siteId}/setup/${prev[0]}`} className="btn">
                Back
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link to={`/sites/${site.siteId}/setup/${next[0]}`} className="btn primary">
                Next: {/^[A-Z][a-z]/.test(next[1]) ? next[1][0].toLowerCase() + next[1].slice(1) : next[1]}
              </Link>
            )}
          </div>
        </section>
      </div>
    </Shell>
  );
}
